// ImageUploaderCropper.tsx
import React, { useCallback, useMemo, useState } from 'react';
import Cropper, { Area } from 'react-easy-crop';
import { uploadFile } from '../../utils/uploadFile';
import { v4 as uuidv4 } from 'uuid';
import { ActionButton } from '../../designSystem/components/ActionButton';

type Props = {
  folder: string;                              // 上傳到哪個資料夾
  onUploaded?: (url: string | null) => void;   // 上傳完成回呼
  maxSizeMB?: number;                          // 預設 3MB
  isSquare?: boolean;                          // 裁切 1:1
  cropEnabled?: boolean;                       // 是否啟用裁切（預設 true）
};

export const ImageUploaderCropper: React.FC<Props> = ({
                                                        folder,
                                                        onUploaded,
                                                        maxSizeMB = 3,
                                                        isSquare = false,
                                                        cropEnabled = true,
                                                      }) => {
  const [rawFile, setRawFile] = useState<File | null>(null);
  const [imgUrl, setImgUrl] = useState<string | null>(null);
  const [crop, setCrop] = useState<{ x: number; y: number }>({ x: 0, y: 0 });
  const [zoom, setZoom] = useState<number>(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<Area | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [errMsg, setErrMsg] = useState<string | null>(null);

  const aspect = useMemo(() => (isSquare ? 1 : 4 / 3), [isSquare]);

  const onSelectFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    setErrMsg(null);
    const file = e.target.files?.[0];
    if (!file) return;

    // 檔案大小檢查
    const maxBytes = (maxSizeMB ?? 3) * 1024 * 1024;
    if (file.size > maxBytes) {
      setErrMsg(`檔案過大：上限 ${maxSizeMB}MB`);
      e.target.value = '';
      return;
    }

    // 檔案型態檢查
    if (!/^image\/(png|jpeg|jpg|webp)$/i.test(file.type)) {
      setErrMsg('只支援 PNG / JPG / JPEG / WEBP 圖片');
      e.target.value = '';
      return;
    }

    setRawFile(file);
    const reader = new FileReader();
    reader.onload = () => setImgUrl(reader.result as string);
    reader.readAsDataURL(file);
  };

  const onCropComplete = useCallback((_: Area, croppedPixels: Area) => {
    setCroppedAreaPixels(croppedPixels);
  }, []);

  const createImage = (url: string): Promise<HTMLImageElement> =>
    new Promise((resolve, reject) => {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = () => resolve(img);
      img.onerror = reject;
      img.src = url;
    });

  // 將裁切區塊輸出為 Blob
  const getCroppedBlob = async (imageSrc: string, pixelCrop: Area): Promise<Blob> => {
    const image = await createImage(imageSrc);
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('Canvas not supported');

    canvas.width = Math.round(pixelCrop.width);
    canvas.height = Math.round(pixelCrop.height);

    ctx.drawImage(
      image,
      pixelCrop.x,
      pixelCrop.y,
      pixelCrop.width,
      pixelCrop.height,
      0,
      0,
      pixelCrop.width,
      pixelCrop.height
    );

    return new Promise<Blob>((resolve, reject) => {
      canvas.toBlob((blob) => {
        if (!blob) return reject(new Error('Canvas toBlob failed'));
        resolve(blob);
      }, 'image/jpeg', 0.92);
    });
  };

  const handleUpload = async () => {
    if (!rawFile) {
      setErrMsg('請先選擇圖片');
      return;
    }

    try {
      setLoading(true);
      setErrMsg(null);

      let fileToUpload: File = rawFile;

      if (cropEnabled) {
        if (!imgUrl || !croppedAreaPixels) {
          setLoading(false);
          setErrMsg('尚未完成裁切');
          return;
        }
        const blob = await getCroppedBlob(imgUrl, croppedAreaPixels);
        fileToUpload = new File(
          [blob],
          `${uuidv4()}-cropped-${rawFile.name.replace(/\s+/g, '_')}`,
          { type: blob.type }
        );
      } else {
        // 不裁切：原檔直接上傳（可視需求加上重新命名）
        fileToUpload = new File(
          [rawFile],
          `${uuidv4()}-${rawFile.name.replace(/\s+/g, '_')}`,
          { type: rawFile.type }
        );
      }

      const url = await uploadFile(fileToUpload, folder);
      onUploaded?.(url);

      // 重置
      setLoading(false);
      reset();
    } catch (err: any) {
      setLoading(false);
      setErrMsg(err?.message ?? '上傳失敗，請稍後再試');
      onUploaded?.(null);
    }
  };

  const reset = () => {
    setErrMsg(null);
    setRawFile(null);
    setImgUrl(null);
    setZoom(1);
    setCrop({ x: 0, y: 0 });
    setCroppedAreaPixels(null);
  };

  return (
    <div style={{ maxWidth: 640, border: '1px solid #000', borderRadius: '10px', padding: 15 }}>
      <div style={{ marginBottom: 12 }}>
        <input
          type="file"
          accept="image/png,image/jpeg,image/jpg,image/webp"
          onChange={onSelectFile}
          disabled={loading}
        />
      </div>

      {/* 預覽 / 裁切區 */}
      {imgUrl && (
        <div
          style={{
            position: 'relative',
            width: '100%',
            height: 360,
            background: '#111',
            borderRadius: 8,
            overflow: 'hidden',
            marginBottom: 12,
          }}
        >
          {cropEnabled ? (
            <Cropper
              image={imgUrl}
              crop={crop}
              zoom={zoom}
              aspect={aspect}
              onCropChange={setCrop}
              onZoomChange={setZoom}
              onCropComplete={onCropComplete}
              restrictPosition={false}
              showGrid={true}
            />
          ) : (
            // 不裁切：只顯示預覽
            <img
              src={imgUrl}
              alt="preview"
              style={{ width: '100%', height: '100%', objectFit: 'contain', background: '#111' }}
            />
          )}
        </div>
      )}

      {/* 縮放滑桿（只在裁切模式顯示） */}
      {imgUrl && cropEnabled && (
        <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginBottom: 12 }}>
          <label style={{ minWidth: 48 }}>縮放</label>
          <input
            type="range"
            min={1}
            max={3}
            step={0.01}
            value={zoom}
            onChange={(e) => setZoom(Number(e.target.value))}
            disabled={loading}
            style={{ flex: 1 }}
          />
        </div>
      )}

      {errMsg && <div style={{ color: '#dc2626', marginBottom: 12 }}>{errMsg}</div>}

      <div style={{ display: 'flex', gap: 8 }}>
        {imgUrl ? (
          <>
            <ActionButton
              label={loading ? (cropEnabled ? '裁切並上傳中...' : '上傳中...') : (cropEnabled ? '裁切並上傳' : '上傳')}
              onClick={handleUpload}
              disabled={loading}
            />
            <ActionButton
              label="重新選擇"
              color="secondary"
              onClick={reset}
              disabled={loading}
            />
          </>
        ) : (
          <div style={{ padding: '8px 14px' }}>
            請先選擇圖片
          </div>
        )}
      </div>
    </div>
  );
};